import { useLocation, Location as RouterLocation } from "react-router-dom";
import { useCallback, useEffect } from "react";
import { navigationChannel, URL_SYNC } from "../utils/broadcastChannel";
import { removeTrailingSlash } from "../utils/url";

interface UrlSyncMessage {
  type: typeof URL_SYNC;
  name: string;
  origin: string;
  pathname: string;
  search: string;
}

export const handleUrlSync = (
  message: UrlSyncMessage,
  location: RouterLocation,
  basePath: string | undefined,
) => {
  if (!basePath) return;
  const { name, pathname, search } = message;
  let iframePath = pathname;

  //TODO: Handle this in a more generic way rather than hardcoding
  if (name === "dashboard") {
    iframePath = pathname.replace("/dash/app", "");
  }

  const currentSearch = new URLSearchParams(location.search);
  const newSearch = new URLSearchParams(search);

  // Merge query params, excluding access_token (or token for dashboard)
  newSearch.delete("access_token");
  newSearch.delete("token");
  currentSearch.forEach((value, key) => {
    if (key !== "access_token" && key !== "token" && !newSearch.has(key)) {
      newSearch.set(key, value);
    }
  });

  // basePath is pre-adjusted in getSublinkRoutes to avoid path doubling when the
  // iframe app's path prefix mirrors the tail of the nav route (e.g. /v2/data/api → /v2/data).
  let cleanRelativePath = iframePath.startsWith("/") ? iframePath : `/${iframePath}`;

  // Strip the last segment of basePath if it appears doubled in cleanRelativePath.
  // e.g., basePath="/v2/dashboard", cleanRelativePath="/dashboard/<id>" → "/<id>"
  // This mirrors the dedup logic in addRelativePath for the URL sync direction.
  const baseSuffix = basePath.match(/\/[^/]+$/)?.[0] ?? "";
  if (baseSuffix && cleanRelativePath.startsWith(baseSuffix + "/")) {
    cleanRelativePath = cleanRelativePath.slice(baseSuffix.length);
  } else if (baseSuffix && cleanRelativePath === baseSuffix) {
    cleanRelativePath = "";
  }
  const finalPath = `${removeTrailingSlash(`${basePath}${cleanRelativePath}`)}${newSearch.toString() ? `?${newSearch.toString()}` : ""}`;
  window.history.replaceState(null, "", finalPath);
};

export const useUrlSyncListener = (basePath: string | undefined) => {
  const location = useLocation();

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.data.type === URL_SYNC) {
        handleUrlSync(event.data, location, basePath);
      }
    },
    [location, basePath],
  );

  const handleMessageFromDexibit = useCallback(
    (event: MessageEvent) => {
      if (event.origin.endsWith("dexibit.com")) {
        handleMessage(event);
      }
    },
    [handleMessage],
  );

  useEffect(() => {
    navigationChannel.addEventListener("message", handleMessage);
    window.addEventListener("message", handleMessageFromDexibit);

    return () => {
      navigationChannel.removeEventListener("message", handleMessage);
      window.removeEventListener("message", handleMessageFromDexibit);
    };
  }, [handleMessage, handleMessageFromDexibit]);
};
